import type { ApolloError } from 'apollo-client';
import { useCallback, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

import { ONBOARDING_EXPERIENCES } from '../../constants/onboarding-state/onboarding-constants';
import { deserializeState, useGetOnboardingState } from '../onboardingState/onboardingState';

export type GetLandOnPathParams = {
	landOnPath?: string;
	improvedGetStartedPath?: string;
	improvedGetStartedWhiteboardPath?: string;
	nthUserGSPage?: string;
	isLoading: boolean;
	error?: ApolloError;
};

/**
 * Hook to get the land on path onboarding state
 * @param skip - Waits to fetch the data until the condition is true
 * @returns landOnPath - The land on path onboarding state
 * @returns loading - The loading state of the query
 * @returns error - The error state of the query
 */
export const useGetLandOnPath = (skip = false): GetLandOnPathParams => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireAnalyticsEvent = useCallback(
		(useOnboardingStateError: ApolloError | undefined) => {
			const attributes = useOnboardingStateError?.message
				? { errorMessage: useOnboardingStateError.message }
				: {};
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					actionSubject: 'landOnPathHook',
					action: useOnboardingStateError ? 'failed' : 'succeeded',
					source: 'useGetLandOnPath',
					attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);
	const hasFiredAnalyticsEventRef = useRef(false);

	const { data, loading, error } = useGetOnboardingState(
		[
			ONBOARDING_EXPERIENCES.LAND_ON_PATH,
			ONBOARDING_EXPERIENCES.IMPROVED_GET_STARTED_PATH,
			ONBOARDING_EXPERIENCES.IMPROVED_GET_STARTED_WHITEBOARD_PATH,
			ONBOARDING_EXPERIENCES.IMPROVED_GET_STARTED_NTH_USER_PATH,
		],
		skip,
	);

	if ((data || error) && !loading && !hasFiredAnalyticsEventRef.current) {
		if (error) {
			markErrorAsHandled(error);
		}
		fireAnalyticsEvent(error);
		hasFiredAnalyticsEventRef.current = true;
	}

	const deserializedData = deserializeState(data);

	return {
		landOnPath: deserializedData.landOnPath,
		improvedGetStartedPath: deserializedData?.improvedGetStartedPath,
		improvedGetStartedWhiteboardPath: deserializedData?.improvedGetStartedWhiteboardPath,
		nthUserGSPage: deserializedData?.nthUserGSPagePath,
		isLoading: loading,
		error,
	};
};
